import React, { useEffect, useState } from "react";
import { Wand2, Loader2, CheckCircle2, AlertTriangle, Rocket, Gauge, SlidersHorizontal, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { PageHeader, StatCard, SectionCard, EmptyState } from "@/components/common";
import { Btn, Select } from "@/components/DataTable";
import { PriorityBadge } from "@/components/StatusBadge";

const scoreTone = (s) => (s >= 80 ? "bg-emerald-100 text-emerald-700" : s >= 60 ? "bg-teal-100 text-teal-700" : s >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700");

function Toggle({ label, checked, onChange, testid, hint }) {
  return (
    <label className="flex items-start justify-between gap-3 py-1.5 cursor-pointer">
      <span className="text-sm text-slate-600">{label}{hint && <span className="block text-xs text-slate-400">{hint}</span>}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="mt-0.5 rounded h-4 w-4 accent-seq-green" />
    </label>
  );
}

export default function AutoScheduler() {
  const [scope, setScope] = useState("all");
  const [scopeId, setScopeId] = useState("");
  const [clients, setClients] = useState([]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selected, setSelected] = useState({});
  const [opts, setOpts] = useState({ honour_preferred: true, load_balance: true, allow_over_capacity: false, horizon_days: 30 });

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const run = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/auto-scheduler/preview", { scope, scope_id: scopeId || null, options: opts });
      setResult(data);
      setSelected(Object.fromEntries(data.proposals.map((p) => [p.visit_id, true])));
    } catch (e) { toast.error(apiError(e)); } finally { setRunning(false); }
  };

  const publish = async () => {
    const proposals = result.proposals.filter((p) => selected[p.visit_id]);
    if (!proposals.length) return toast.error("Select at least one proposal");
    setPublishing(true);
    try {
      const { data } = await api.post("/auto-scheduler/publish", { proposals });
      toast.success(`Published ${data.published} visits to the calendar`);
      setResult(null);
    } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
  };

  const allSelected = result && result.proposals.length > 0 && result.proposals.every((p) => selected[p.visit_id]);
  const setOpt = (k, v) => setOpts((o) => ({ ...o, [k]: v }));

  return (
    <div>
      <PageHeader title="Intelligent Auto-Scheduler" subtitle="Best-fit scoring · skills matching · workload balancing — review & approve before the live calendar changes" icon={Wand2} />

      <div className="grid md:grid-cols-3 gap-5 mb-5">
        <SectionCard className="md:col-span-2">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Scope</label>
              <Select value={scope} onChange={(e) => { setScope(e.target.value); setScopeId(""); }} className="mt-1 w-48" data-testid="autoscheduler-scope">
                <option value="all">All unscheduled work</option>
                <option value="client">Selected client</option>
              </Select>
            </div>
            {scope === "client" && (
              <div>
                <label className="text-xs font-medium text-slate-600">Client</label>
                <Select value={scopeId} onChange={(e) => setScopeId(e.target.value)} className="mt-1 w-56">
                  <option value="">Select client</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                </Select>
              </div>
            )}
            <Btn variant="forest" onClick={run} disabled={running} data-testid="run-autoscheduler-btn">
              {running ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Generate proposal
            </Btn>
          </div>
        </SectionCard>

        <SectionCard title={<span className="flex items-center gap-1.5"><SlidersHorizontal size={14} /> Scheduling rules</span>}>
          <div className="divide-y divide-slate-100">
            <Toggle label="Honour preferred consultant" hint="Weight the template's preferred person higher" checked={opts.honour_preferred} onChange={(v) => setOpt("honour_preferred", v)} testid="opt-honour-preferred" />
            <Toggle label="Balance workload" hint="Favour the least-loaded consultant" checked={opts.load_balance} onChange={(v) => setOpt("load_balance", v)} testid="opt-load-balance" />
            <Toggle label="Allow over-capacity" hint="Permit exceeding daily job limit (flagged)" checked={opts.allow_over_capacity} onChange={(v) => setOpt("allow_over_capacity", v)} testid="opt-allow-overcapacity" />
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-slate-600">Search horizon</span>
              <Select value={opts.horizon_days} onChange={(e) => setOpt("horizon_days", +e.target.value)} className="w-28" data-testid="opt-horizon">
                {[14, 30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
              </Select>
            </div>
          </div>
        </SectionCard>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <StatCard label="Proposed" value={result.summary.scheduled} icon={CheckCircle2} tone="green" />
            <StatCard label="Avg fit score" value={result.summary.avg_score} icon={Gauge} tone="teal" />
            <StatCard label="With warnings" value={result.summary.with_warnings} icon={AlertTriangle} tone={result.summary.with_warnings ? "amber" : "default"} />
            <StatCard label="Unresolved" value={result.summary.unresolved} icon={AlertTriangle} tone={result.summary.unresolved ? "red" : "default"} />
          </div>

          <SectionCard title="Proposed schedule" noPad className="mb-5"
            action={result.proposals.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-500 flex items-center gap-1.5">
                  <input type="checkbox" checked={allSelected} onChange={(e) => setSelected(Object.fromEntries(result.proposals.map((p) => [p.visit_id, e.target.checked])))} /> Select all
                </label>
                <Btn onClick={publish} disabled={publishing} data-testid="publish-schedule-btn">
                  {publishing ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Approve & publish
                </Btn>
              </div>
            )}>
            {result.proposals.length === 0 ? <EmptyState icon={CheckCircle2} title="Nothing to schedule" /> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-2 w-8"></th>
                      <th className="text-left px-4 py-2 font-medium">Job</th>
                      <th className="text-left px-4 py-2 font-medium">Priority</th>
                      <th className="text-left px-4 py-2 font-medium">Proposed</th>
                      <th className="text-left px-4 py-2 font-medium">Consultant</th>
                      <th className="text-left px-4 py-2 font-medium">Fit</th>
                      <th className="text-left px-4 py-2 font-medium">Load</th>
                      <th className="text-left px-4 py-2 font-medium">Notes & warnings</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {result.proposals.map((p) => (
                      <tr key={p.visit_id} data-testid={`proposal-${p.visit_id}`} className={`hover:bg-slate-50 ${p.warnings && p.warnings.length ? "bg-amber-50/40" : ""}`}>
                        <td className="px-4 py-2"><input type="checkbox" checked={!!selected[p.visit_id]} onChange={(e) => setSelected({ ...selected, [p.visit_id]: e.target.checked })} data-testid={`select-${p.visit_id}`} /></td>
                        <td className="px-4 py-2"><div className="font-medium text-slate-700">{p.job_title}</div><div className="text-xs text-slate-400">{p.client_name} · {p.site_name}</div></td>
                        <td className="px-4 py-2"><PriorityBadge priority={p.priority} /></td>
                        <td className="px-4 py-2 text-slate-700 font-medium text-xs">{fmtDate(p.proposed_date)} {p.proposed_time}
                          {p.original_date && p.original_date.slice(0, 10) !== p.proposed_date && <div className="text-[11px] text-slate-400">was {fmtDate(p.original_date)}</div>}
                        </td>
                        <td className="px-4 py-2"><span className="inline-flex items-center gap-1.5 text-slate-600">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.consultant_colour }} />{p.consultant_name}
                          {p.reason && p.reason.includes("preferred") && <Star size={12} className="text-amber-500 fill-amber-400" />}
                        </span></td>
                        <td className="px-4 py-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${scoreTone(p.fit_score)}`} data-testid={`fit-${p.visit_id}`}>{p.fit_score}</span></td>
                        <td className="px-4 py-2 text-xs text-slate-500">{p.resulting_load}/{p.max_jobs}</td>
                        <td className="px-4 py-2 text-xs">
                          <div className="text-slate-500">{p.reason}</div>
                          {p.warnings && p.warnings.map((w, i) => (
                            <div key={i} className="text-amber-700 flex items-center gap-1 mt-0.5"><AlertTriangle size={11} /> {w}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {result.unresolved.length > 0 && (
            <SectionCard title="Unresolved conflicts" noPad>
              <div className="divide-y divide-slate-100">
                {result.unresolved.map((u) => (
                  <div key={u.visit_id} className="px-4 py-2.5 flex items-center justify-between text-sm">
                    <span className="text-slate-700">{u.job_title} <span className="text-slate-400">· {u.client_name}</span></span>
                    <span className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle size={13} /> {u.reason}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}
